import type { EncryptionService } from './encryption.interface.ts'
import type {
  EncryptStringParams,
  EncryptFileParams,
  EncryptionResult,
  DecryptParams,
  DecryptionResult,
  AuthSession,
} from './encryption.types.ts'

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

let proxyUrl = ''
let connected = false
let authState: AuthSession | null = null

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function sha256Hex(data: Uint8Array): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', data.buffer as ArrayBuffer)
  const bytes = new Uint8Array(hash)
  return (
    '0x' +
    Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  )
}

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

/**
 * Build the human-readable challenge the benefactor signs to authorize an
 * encrypt call. The address is embedded so the signature is bound to this
 * benefactor; the timestamp lets the proxy reject stale signatures.
 */
function buildEncryptChallenge(benefactorAddress: string): string {
  return [
    'BitsLegacy Encryption Authorization',
    '',
    `Wallet: ${benefactorAddress}`,
    `Issued: ${new Date().toISOString()}`,
    '',
    'Sign this message to authorize encryption of vault data.',
    'This does NOT trigger a blockchain transaction.',
  ].join('\n')
}

/**
 * Build the human-readable challenge the beneficiary signs to request a
 * one-time decryption nonce. Both the wallet and the vault id are embedded
 * so the proxy can bind the signature to the request parameters.
 */
function buildGetNonceChallenge(
  vaultId: string | number,
  beneficiaryAddress: string,
): string {
  return [
    'BitsLegacy Nonce Request',
    '',
    `Vault ID: ${vaultId}`,
    `Wallet: ${beneficiaryAddress}`,
    `Issued: ${new Date().toISOString()}`,
    '',
    'Sign this message to request a one-time decryption nonce.',
    'This does NOT trigger a blockchain transaction.',
  ].join('\n')
}

async function proxyPost(
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const res = await fetch(proxyUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>

  if (!res.ok) {
    const errMsg =
      (json.error as string) || `Lit proxy responded with ${res.status}`
    throw new Error(errMsg)
  }

  return json
}

// ---------------------------------------------------------------------------
// Service factory
// ---------------------------------------------------------------------------

export function createLitEncryptionService(): EncryptionService {
  return {
    async connect() {
      if (connected) return

      const { getLitProxyUrl } = await import('./lit.config.ts')
      proxyUrl = getLitProxyUrl()

      connected = true
    },

    async disconnect() {
      authState = null
      connected = false
    },

    isReady() {
      return connected
    },

    async authenticate(params) {
      if (!connected) {
        throw new Error(
          'Encryption service not connected. Call connect() first.',
        )
      }
      if (!params.walletAddress) {
        throw new Error('Wallet address is required for authentication.')
      }

      const expiration =
        params.expiration ?? new Date(Date.now() + 1000 * 60 * 60).toISOString()

      const challengeMessage = [
        'BitsLegacy Decryption Authorization',
        '',
        `Wallet: ${params.walletAddress}`,
        `Issued: ${new Date().toISOString()}`,
        `Expires: ${expiration}`,
        '',
        'Sign this message to prove you own this wallet.',
        'This does NOT trigger a blockchain transaction.',
      ].join('\n')

      const signature = await params.signMessage(challengeMessage)

      authState = {
        isAuthenticated: true,
        expiration,
        walletAddress: params.walletAddress,
        signature,
        message: challengeMessage,
        signMessage: params.signMessage,
      }

      return authState
    },

    async getNonce(
      vaultId: string | number,
      beneficiaryAddress: string,
      signMessage: (message: string) => Promise<string>,
    ): Promise<string> {
      const challengeMessage = buildGetNonceChallenge(
        vaultId,
        beneficiaryAddress,
      )
      const signature = await signMessage(challengeMessage)
      const result = await proxyPost({
        action: 'get-nonce',
        vaultId,
        beneficiaryAddress,
        signature,
        challengeMessage,
      })
      if (!result.nonce) {
        throw new Error('Failed to get security nonce from proxy')
      }
      return result.nonce as string
    },

    async encryptString(params: EncryptStringParams) {
      if (!connected) {
        throw new Error(
          'Encryption service not connected. Call connect() first.',
        )
      }

      const dataBytes = new TextEncoder().encode(params.data)
      const dataToEncryptHash = await sha256Hex(dataBytes)
      const b64 = uint8ToBase64(dataBytes)

      const challengeMessage = buildEncryptChallenge(params.benefactorAddress)
      const signature = await params.signMessage(challengeMessage)

      const result = await proxyPost({
        action: 'encrypt',
        data: b64,
        benefactorAddress: params.benefactorAddress,
        signature,
        challengeMessage,
      })

      if (!result.ciphertext) {
        throw new Error('Encryption failed: no ciphertext returned.')
      }

      return {
        ciphertext: result.ciphertext as string,
        dataToEncryptHash,
      } satisfies EncryptionResult
    },

    async encryptFile(params: EncryptFileParams) {
      if (!connected) {
        throw new Error(
          'Encryption service not connected. Call connect() first.',
        )
      }

      let dataBytes: Uint8Array
      if (params.file instanceof Uint8Array) {
        dataBytes = params.file
      } else {
        const buffer = await (params.file as Blob).arrayBuffer()
        dataBytes = new Uint8Array(buffer)
      }

      if (dataBytes.length === 0) {
        throw new Error('Cannot encrypt empty file.')
      }

      const dataToEncryptHash = await sha256Hex(dataBytes)
      const b64 = uint8ToBase64(dataBytes)

      const challengeMessage = buildEncryptChallenge(params.benefactorAddress)
      const signature = await params.signMessage(challengeMessage)

      const result = await proxyPost({
        action: 'encrypt',
        data: b64,
        benefactorAddress: params.benefactorAddress,
        signature,
        challengeMessage,
      })

      if (!result.ciphertext) {
        throw new Error('Encryption failed: no ciphertext returned.')
      }

      return {
        ciphertext: result.ciphertext as string,
        dataToEncryptHash,
      } satisfies EncryptionResult
    },

    async decrypt(params: DecryptParams) {
      if (!connected) {
        throw new Error(
          'Encryption service not connected. Call connect() first.',
        )
      }
      if (!authState) {
        throw new Error('Not authenticated. Call authenticate() first.')
      }

      // Fetch a fresh nonce for this decryption. The proxy now requires a
      // beneficiary signature on the get-nonce request, so we pass the
      // wallet signer captured at authenticate() time.
      const nonce = await this.getNonce(
        String(params.vaultId),
        params.beneficiaryAddress,
        authState.signMessage,
      )

      // Generate a one-time challenge message with the nonce
      const challengeMessage = [
        'BitsLegacy Decryption Authorization',
        '',
        `Nonce: ${nonce}`,
        `Vault ID: ${params.vaultId}`,
        `Wallet: ${authState.walletAddress}`,
        `Issued: ${new Date().toISOString()}`,
        '',
        'This signature is valid for 60 seconds and can only be used once.',
      ].join('\n')

      // Request user signature for the nonce-enabled challenge
      // We need a signMessage function. If not in params, we might need to store it in authState or pass it.
      // Looking at authenticate(), it received signMessage in params.
      // Let's assume we can use a stored signer or ask the user to provide it.
      // Wait, DecryptParams doesn't have signMessage.
      // I'll check if I should add it to DecryptParams or if it's available elsewhere.

      // For now, I'll assume we need to pass signMessage to decrypt() or have it available.
      // Let's check DecryptParams definition.

      const signature = await authState.signMessage(challengeMessage)

      // BL-X-01: contractAddress + chain intentionally omitted; the proxy
      // anchors them server-side from CONTRACT_ADDRESS / CONTRACT_NETWORK.
      const result = await proxyPost({
        action: 'decrypt',
        ciphertext: params.ciphertext,
        beneficiaryAddress: params.beneficiaryAddress,
        vaultId: params.vaultId,
        signature: signature,
        challengeMessage: challengeMessage,
      })

      if (!result.plaintext && result.plaintext !== '') {
        throw new Error('Decryption failed: no plaintext returned.')
      }

      const plaintextB64 = result.plaintext as string
      const decryptedData = base64ToUint8(plaintextB64)
      const decryptedString = new TextDecoder().decode(decryptedData)

      return { decryptedData, decryptedString } satisfies DecryptionResult
    },

    async decryptToString(params: DecryptParams) {
      const { decryptedString } = await this.decrypt(params)
      return decryptedString ?? ''
    },

    async decryptToFile(params: DecryptParams) {
      const { decryptedData } = await this.decrypt(params)
      return new Blob([decryptedData.buffer as ArrayBuffer])
    },
  }
}
